import { useLayoutEffect, useRef, useState, type PointerEvent } from 'react';

import { fontStyles } from '@/styles/typography';

import { ChartCrosshairAxisLabel } from './ChartCrosshairAxisLabel';
import type { ChartPlot, ChartPriceLine, ChartPriceLineStyle } from './types';
import { invertLinearScale, type LinearScale } from './utils/scales';

type ChartPriceLinesProps = {
  lines: ChartPriceLine[];
  plot: ChartPlot;
  yDomain: [number, number];
  yScale: LinearScale;
  /** SVG viewport height, used to map pointer positions into SVG space while dragging. */
  height: number;
  formatValue?: (value: number) => string;
};

const DRAG_HANDLE_WIDTH = 12;
const LABEL_OFFSET = 4;

/** Dash patterns scale with stroke width so thicker lines keep the same rhythm. */
const getDashArray = (style: ChartPriceLineStyle, width: number) => {
  switch (style) {
    case 'solid':
      return undefined;
    case 'dashed':
      return `${width * 4} ${width * 4}`;
    case 'dotted':
      return `${width} ${width * 2}`;
    default: {
      const exhaustive: never = style;
      return exhaustive;
    }
  }
};

const LABEL_HEIGHT = 16;
const LABEL_PADDING_X = 4;
const LABEL_WIDTH_DEFAULT = 48;

type ChartPriceLineLabelProps = {
  text: string;
  color: string;
  plot: ChartPlot;
  /** Y of the price line; the label sits `gap` px above or below it. */
  lineY: number;
  gap: number;
  position: NonNullable<ChartPriceLine['labelPosition']>;
  align: NonNullable<ChartPriceLine['labelAlign']>;
  /** SVG viewport height, used to keep the label off the top and bottom edges. */
  height: number;
};

/** Caption on a translucent pill so it stays legible over candles. */
const ChartPriceLineLabel = ({
  text,
  color,
  plot,
  lineY,
  gap,
  position,
  align,
  height,
}: ChartPriceLineLabelProps) => {
  const [width, setWidth] = useState(LABEL_WIDTH_DEFAULT);
  const textRef = useRef<SVGTextElement | null>(null);

  useLayoutEffect(() => {
    const textWidth = textRef.current?.getBBox().width;
    if (!textWidth) return;

    const nextWidth = Math.ceil(textWidth) + LABEL_PADDING_X * 2;
    setWidth((currentWidth) => (currentWidth === nextWidth ? currentWidth : nextWidth));
  }, [text]);

  const x =
    align === 'left'
      ? plot.left + LABEL_OFFSET
      : align === 'center'
        ? plot.left + (plot.width - width) / 2
        : plot.right - LABEL_OFFSET - width;
  // The plot's vertical padding is shorter than the label, so a line resting against
  // the top or bottom of the plot would push its label off the viewport.
  const y = Math.min(
    Math.max(position === 'above' ? lineY - gap - LABEL_HEIGHT : lineY + gap, 0),
    height - LABEL_HEIGHT,
  );

  return (
    <g data-testid="chart-price-line-label" pointerEvents="none">
      <rect
        x={x}
        y={y}
        width={width}
        height={LABEL_HEIGHT}
        rx={3}
        fill="var(--color-token-alphalight-300)"
      />
      <text
        ref={textRef}
        x={x + width / 2}
        y={y + LABEL_HEIGHT / 2}
        textAnchor="middle"
        dominantBaseline="central"
        className={fontStyles({ variant: 'CAPTION' })}
        fill={color}
      >
        {text}
      </text>
    </g>
  );
};

export const ChartPriceLines = ({
  lines,
  plot,
  yDomain,
  yScale,
  height,
  formatValue,
}: ChartPriceLinesProps) => {
  const [draggingKey, setDraggingKey] = useState<string>();

  const toValue = (event: PointerEvent<SVGLineElement>) => {
    const bounds = event.currentTarget.ownerSVGElement?.getBoundingClientRect();
    if (!bounds || bounds.height === 0) return undefined;

    const svgY = ((event.clientY - bounds.top) / bounds.height) * height;
    const clampedY = Math.min(Math.max(svgY, plot.top), plot.bottom);
    return invertLinearScale(clampedY, yDomain, [plot.bottom, plot.top]);
  };

  return (
    <>
      {lines.map(
        ({
          key,
          value,
          label,
          color,
          lineStyle = 'dashed',
          lineWidth = 1,
          labelPosition = 'above',
          labelAlign = 'right',
          onValueChange,
        }) => {
          if (!Number.isFinite(value)) return null;

          const y = yScale(value);
          if (y < plot.top || y > plot.bottom) return null;

          return (
            <g key={key} data-testid="chart-price-line">
              <line
                x1={plot.left}
                x2={plot.right}
                y1={y}
                y2={y}
                stroke={color}
                strokeWidth={lineWidth}
                strokeDasharray={getDashArray(lineStyle, lineWidth)}
                pointerEvents="none"
              />
              {label && (
                <ChartPriceLineLabel
                  text={label}
                  color={color}
                  plot={plot}
                  lineY={y}
                  gap={LABEL_OFFSET + lineWidth / 2}
                  position={labelPosition}
                  align={labelAlign}
                  height={height}
                />
              )}
              {formatValue && (
                <ChartCrosshairAxisLabel
                  axis="y"
                  plot={plot}
                  position={y}
                  label={formatValue(value)}
                  fill={color}
                  textFill="var(--primitives-color-gray-100)"
                />
              )}
              {onValueChange && (
                <line
                  data-testid="chart-price-line-handle"
                  x1={plot.left}
                  x2={plot.right}
                  y1={y}
                  y2={y}
                  stroke="transparent"
                  strokeWidth={Math.max(DRAG_HANDLE_WIDTH, lineWidth)}
                  className="cursor-ns-resize touch-none"
                  onPointerDown={(event) => {
                    // Capture keeps move/up events flowing to the handle once the pointer
                    // leaves its thin hit area mid-drag.
                    event.currentTarget.setPointerCapture?.(event.pointerId);
                    setDraggingKey(key);
                  }}
                  onPointerMove={(event) => {
                    if (draggingKey !== key) return;
                    const nextValue = toValue(event);
                    if (nextValue != null) onValueChange(nextValue);
                  }}
                  onPointerUp={() => setDraggingKey(undefined)}
                  onPointerCancel={() => setDraggingKey(undefined)}
                />
              )}
            </g>
          );
        },
      )}
    </>
  );
};
